import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, EmptyState, Skeleton } from '@/components';
import { InboxFileDialog } from '@/components/personal/InboxFileDialog';
import { InboxPage } from '@/components/personal/InboxPage';
import { announce } from '@/lib/announce';
import { useMoveDestinations } from '@/lib/cross-board-move';
import {
  inboxBackgroundQueryOptions,
  useCreateInboxCard,
  useCreateInboxChecklist,
  useCaptureInboxCard,
  useArchiveInboxCard,
  useFileInboxCard,
  inboxEmailAddressQueryOptions,
  inboxCardsQueryOptions,
  useRegenerateInboxEmailAddress,
  useRestoreInboxCard,
  useSetInboxBackground,
  useSetInboxCardDueDate,
  type InboxCardsClientQuery,
} from '@/lib/personal-cards';
import { useConnectedApps } from '@/lib/external-access';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { signInHref, useSession } from '@/lib/session';

export const Route = createFileRoute('/inbox')({
  component: InboxRoute,
});

function InboxRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const session = useSession();
  const { t } = useInterfaceLanguage();
  const signedIn = !session.isSignedOut && !session.isPending && session.profile !== null;
  const query = inboxQueryFromSearch(location.searchStr);
  const inbox = useQuery({ ...inboxCardsQueryOptions(query), enabled: signedIn });
  const inboxEmailAddress = useQuery({
    ...inboxEmailAddressQueryOptions(),
    enabled: signedIn,
  });
  const inboxBackground = useQuery({
    ...inboxBackgroundQueryOptions(),
    enabled: signedIn,
  });
  const connectedApps = useConnectedApps(signedIn);
  const createCard = useCreateInboxCard();
  const captureCard = useCaptureInboxCard();
  const archiveCard = useArchiveInboxCard();
  const restoreCard = useRestoreInboxCard();
  const regenerateAddress = useRegenerateInboxEmailAddress();
  const setBackground = useSetInboxBackground();
  const selectedCardId = new URLSearchParams(location.searchStr).get('card');
  const [fileCardId, setFileCardId] = useState<string | null>(null);
  const selectedCard = inbox.data?.find((card) => card.id === selectedCardId) ?? null;
  const fileTarget = inbox.data?.find((card) => card.id === fileCardId) ?? null;
  const fileTargetTitle =
    fileTarget === null || fileTarget.title.trim() === ''
      ? t('inbox.untitledCard')
      : fileTarget.title;
  const dueDate = useSetInboxCardDueDate(selectedCard?.id ?? '');
  const createChecklist = useCreateInboxChecklist(selectedCard?.id ?? '');
  const fileCard = useFileInboxCard();
  const filing = useMoveDestinations(fileTarget !== null, session.profile?.id);

  function setSelectedCard(cardId: string | null): void {
    const params = new URLSearchParams(location.searchStr);
    if (cardId === null) {
      params.delete('card');
    } else {
      params.set('card', cardId);
    }
    const search = params.toString();
    router.history.replace(`${location.pathname}${search === '' ? '' : `?${search}`}`);
    router.history.flush();
  }

  function setInboxQuery(next: InboxCardsClientQuery): void {
    const params = new URLSearchParams(location.searchStr);
    const normalized = inboxQuerySearchParams(next);
    for (const key of INBOX_QUERY_KEYS) params.delete(key);
    for (const [key, value] of normalized) params.set(key, value);
    const search = params.toString();
    router.history.replace(`${location.pathname}${search === '' ? '' : `?${search}`}`);
    router.history.flush();
  }

  if (session.isSignedOut) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="neutral"
          size="panel"
          title={t('inbox.signInTitle')}
          body={t('inbox.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({ pathname: '/inbox', search: location.searchStr })}
            >
              {t('inbox.signInAction')}
            </Button>
          }
        />
      </main>
    );
  }

  if (session.isPending) {
    return (
      <main className="mx-auto grid w-full max-w-4xl gap-3 px-4 py-6 sm:px-6">
        <Skeleton shape="block" />
        <Skeleton shape="row" />
        <Skeleton shape="row" />
      </main>
    );
  }

  if (session.profile === null) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="error"
          size="panel"
          title={t('inbox.loadFailedTitle')}
          body={t('inbox.genericError')}
        />
      </main>
    );
  }

  return (
    <>
      <InboxPage
        cards={inbox.data ?? []}
        query={query}
        emailAddress={inboxEmailAddress.data}
        background={inboxBackground.data}
        connectedApps={connectedApps.data?.connectedApps}
        loading={inbox.isPending}
        error={inbox.error?.message ?? null}
        creating={createCard.isPending}
        captureBusy={captureCard.isPending}
        captureError={captureCard.error?.message}
        regeneratingAddress={regenerateAddress.isPending}
        emailAddressError={inboxEmailAddress.error?.message}
        backgroundSaving={setBackground.isPending}
        backgroundError={inboxBackground.error?.message ?? setBackground.error?.message}
        archiveBusyId={archiveCard.isPending ? archiveCard.variables?.cardId : null}
        restoreBusyId={restoreCard.isPending ? restoreCard.variables?.cardId : null}
        selectedCardId={selectedCardId}
        onQueryChange={setInboxQuery}
        onCreateCard={(title) => {
          createCard.mutate(title, {
            onSuccess: (card) => {
              announce(t('inbox.announce.cardAdded', { card: card.title }));
            },
          });
        }}
        onCapture={(value) => {
          captureCard.mutate(
            { ...value, attachments: [] },
            {
              onSuccess: (card) => {
                announce(t('inbox.announce.captureSaved', { card: card.title }));
              },
            },
          );
        }}
        onRegenerateAddress={() => {
          regenerateAddress.mutate(undefined, {
            onSuccess: () => announce(t('inbox.announce.addressRegenerated')),
          });
        }}
        onSetBackground={(patch) => {
          setBackground.mutate(patch, {
            onSuccess: () => announce(t('inbox.announce.backgroundSaved')),
          });
        }}
        onArchiveCard={(cardId) => {
          const card = inbox.data?.find((item) => item.id === cardId);
          archiveCard.mutate(
            { cardId },
            {
              onSuccess: () => {
                announce(
                  t('inbox.announce.cardArchived', {
                    card: card?.title ?? t('inbox.untitledCard'),
                  }),
                );
                if (selectedCardId === cardId) setSelectedCard(null);
                void inbox.refetch();
              },
            },
          );
        }}
        onRestoreCard={(cardId) => {
          const card = inbox.data?.find((item) => item.id === cardId);
          restoreCard.mutate(
            { cardId },
            {
              onSuccess: () => {
                announce(
                  t('inbox.announce.cardRestored', {
                    card: card?.title ?? t('inbox.untitledCard'),
                  }),
                );
                void inbox.refetch();
              },
            },
          );
        }}
        onOpenCard={(cardId) => setSelectedCard(cardId)}
        onCloseCard={() => setSelectedCard(null)}
        onSetDueDate={(_cardId, dueAt) => {
          dueDate.mutate(
            { dueAt },
            {
              onSuccess: () => announce(t('inbox.announce.dueDateSaved')),
            },
          );
        }}
        onCreateChecklist={(_cardId, title) => {
          createChecklist.mutate(title, {
            onSuccess: () => announce(t('inbox.announce.checklistAdded')),
          });
        }}
        onOpenFileDialog={(cardId) => {
          setSelectedCard(null);
          setFileCardId(cardId);
        }}
        dueSaving={dueDate.isPending}
        checklistSaving={createChecklist.isPending}
      />
      {fileTarget !== null ? (
        <InboxFileDialog
          open={fileTarget !== null}
          cardTitle={fileTargetTitle}
          boards={filing.boards}
          listsByBoardId={filing.listsByBoardId}
          cardsByListId={filing.cardsByListId}
          loading={filing.loading}
          busy={fileCard.isPending}
          error={fileCard.error?.message ?? null}
          onCancel={() => setFileCardId(null)}
          onFile={(input) => {
            fileCard.mutate(
              {
                cardId: fileTarget.id,
                boardId: input.boardId,
                listId: input.listId,
                previousCardId: input.previousCardId,
                nextCardId: input.nextCardId,
              },
              {
                onSuccess: (card) => {
                  announce(
                    t('inbox.announce.cardFiled', {
                      card: fileTargetTitle,
                    }),
                  );
                  setFileCardId(null);
                  void navigate({ href: card.path });
                },
              },
            );
          }}
        />
      ) : null}
    </>
  );
}

const INBOX_QUERY_KEYS = [
  'keyword',
  'createdFrom',
  'createdTo',
  'completed',
  'dueFrom',
  'dueTo',
  'archived',
  'sort',
] as const;

function valueFromSearch(
  params: URLSearchParams,
  key: (typeof INBOX_QUERY_KEYS)[number],
): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value === '' ? undefined : value;
}

function inboxQueryFromSearch(search: string): InboxCardsClientQuery {
  const params = new URLSearchParams(search);
  const completed = valueFromSearch(params, 'completed');
  const sort = valueFromSearch(params, 'sort');
  return {
    keyword: valueFromSearch(params, 'keyword'),
    createdFrom: valueFromSearch(params, 'createdFrom'),
    createdTo: valueFromSearch(params, 'createdTo'),
    completed:
      completed === 'complete' || completed === 'incomplete' ? completed : undefined,
    dueFrom: valueFromSearch(params, 'dueFrom'),
    dueTo: valueFromSearch(params, 'dueTo'),
    archived: params.get('archived') === 'true',
    sort: sort === 'oldest' || sort === 'alphabetical' ? sort : undefined,
  };
}

function inboxQuerySearchParams(query: InboxCardsClientQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.keyword !== undefined && query.keyword.trim() !== '') {
    params.set('keyword', query.keyword.trim());
  }
  if (query.createdFrom !== undefined) params.set('createdFrom', query.createdFrom);
  if (query.createdTo !== undefined) params.set('createdTo', query.createdTo);
  if (query.completed !== undefined && query.completed !== 'any') {
    params.set('completed', query.completed);
  }
  if (query.dueFrom !== undefined) params.set('dueFrom', query.dueFrom);
  if (query.dueTo !== undefined) params.set('dueTo', query.dueTo);
  if (query.archived === true) params.set('archived', 'true');
  if (query.sort !== undefined && query.sort !== 'newest') params.set('sort', query.sort);
  return params;
}
